import usePageSEO from '../hooks/usePageSEO';
import { ProductSchema } from '../components/JsonLd';

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";
const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

export const UseCasePage = () => {
  usePageSEO({
    title: 'Use Cases | Accounts Payable Automation | Plouton AI',
    description: 'Learn how you can easily automate accounts payable workflows. Our AI agents handle your repetitive tasks exactly like your own team members do today.',
    path: '/use-cases/accounts-payable-automation'
  });

  return (
    <div className="min-h-screen bg-white pt-[108px]">
      <ProductSchema
        name="Accounts Payable Automation"
        description="Automate AP end-to-end with Plouton AI — from invoice capture to three-way matching and payment scheduling."
        url="https://plouton.co/use-cases/accounts-payable-automation"
      />
      {/* Article */}
      <article className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/guides"
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-700 transition-colors mb-10"
              data-testid="back-to-resources"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              All resources
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              Automating Accounts Payable with Browser Agents
            </h1>
            <div className="text-sm text-slate-400 mb-12">
              March 19, 2026 · Plouton AI Team
            </div>

            {/* Content */}
            <div className="space-y-10 text-slate-600 leading-relaxed">
              {/* The Problem */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">The Problem</h2>
                <p className="mb-4">
                  Accounts payable teams spend 60–80% of their time on repetitive manual tasks: downloading invoices 
                  from portals, keying data into ERPs, chasing approvals over email, and reconciling payment batches. 
                  These processes are error-prone, slow, and don't scale.
                </p>
                <p>
                  Traditional automation approaches require deep API integrations with every vendor portal and ERP — a 
                  project that takes months and breaks with every system update.
                </p>
              </section>

              {/* How Plouton Solves It */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">How Plouton Solves It</h2>
                <p>
                  Plouton's browser agents operate your AP workflow the same way a human operator would — but continuously, 
                  accurately, and with a full audit trail.
                </p>
              </section>

              {/* Invoice Ingestion */}
              <section>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Invoice Ingestion</h3>
                <p className="mb-3">
                  The agent monitors vendor portals, email inboxes, and shared drives for incoming invoices. It:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">Downloads invoices from supplier portals (Ariba, Coupa, Taulia, etc.)</li>
                  <li className="list-disc text-slate-500">Extracts line-item data using document understanding</li>
                  <li className="list-disc text-slate-500">Validates against purchase orders and contracts</li>
                  <li className="list-disc text-slate-500">Flags discrepancies for human review</li>
                </ul>
              </section>

              {/* Three-Way Matching */}
              <section>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Three-Way Matching</h3>
                <p className="mb-3">
                  Once invoice data is extracted, the agent performs automated three-way matching:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Purchase Order</strong> — Does the invoice match an approved PO?
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Goods Receipt</strong> — Were the goods or services actually received?
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Invoice Amount</strong> — Does the total match within tolerance?
                  </li>
                </ul>
                <p className="mt-3">
                  Exceptions are routed to the appropriate approver with full context attached.
                </p>
              </section>

              {/* Payment Scheduling */}
              <section>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">Payment Scheduling</h3>
                <p className="mb-3">
                  Approved invoices are automatically queued for payment based on your configured rules:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">Early payment discount capture</li>
                  <li className="list-disc text-slate-500">Cash flow optimization</li>
                  <li className="list-disc text-slate-500">Vendor payment term compliance</li>
                  <li className="list-disc text-slate-500">Batch processing windows</li>
                </ul>
              </section>

              {/* Results */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Results</h2>
                <p className="mb-3">Companies using Plouton for AP automation typically see:</p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">85% reduction</strong> in manual invoice processing time
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">99.2% accuracy</strong> in data extraction and matching
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">40% faster</strong> payment cycle times
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Zero</strong> integration maintenance overhead
                  </li>
                </ul>
              </section>

              {/* Getting Started */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Getting Started</h2>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">
                    Read the <Link to="/guides/getting-started" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">setup guide</Link> to connect your first workspace
                  </li>
                  <li className="list-disc text-slate-500">
                    <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">Request access</a> to start your pilot
                  </li>
                </ul>
              </section>
            </div>
          </motion.div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default UseCasePage;
